import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import CreateNewShortAnswerDesktop from './CreateNewShortAnswerDesktop';
import Toast from '../../Toast';
import { useShortAnswers } from '../../state/hooks';
import { createShortAnswer } from '../../../service';
import useNavigationBlock from '../../../hooks/useNavigationBlock';
import GeneralPopup from '../../GeneralPopup';
import { getUserId } from '../../../userLocalDetails';
import { useProfile } from '../../state/hooks';
import { useUserStates } from '../../../hooks/useUserStates';
import { buildMetadata } from '../../../utils/subjects';
import { useSubjects } from '../../../hooks/useSubjects';

const getDefaultShortAnswer = () => ({
  title: 'Untitled Short Answer',
  highestMarks: 4,
  criterias: [
    { marks: 4, criteria: 'Analyses effectively how the composer represents the idea in the question' },
    { marks: 3, criteria: 'Criteria 2' },
    { marks: 2, criteria: 'Criteria 3' },
  ],
  years: [],
  subjects: [],
  taskTypes: [],
  responseTypes: [],
  ownerId: getUserId(),
});

export default function CreateNewShortAnswerRoot() {
  const {
    setIsNavigationBlocked,
    isShowNavigationBlockPopup,
    confirmNavigationChange,
    cancelNavigationPopup,
  } = useNavigationBlock();

  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const { rawSubjects: subjectsList } = useSubjects(userStates);
  const [shortAnswer, setShortAnswer] = useState(getDefaultShortAnswer);
  const [rangesEnabled, setRangesEnabled] = useState(false);
  const [expandedRangeRows, setExpandedRangeRows] = useState(new Set());

  const { resetData: resetShortAnswers } = useShortAnswers();

  const handleTitleChange = (event) => {
    setIsNavigationBlocked(true);
    setShortAnswer({ ...shortAnswer, title: event.target.value });
  };

  // Empty string | null | undefined | non-numeric -> null so inputs can
  // briefly hold an empty value while the teacher retypes. parseInt-|| 0
  // would coerce empty to 0 mid-edit and the input would refuse to clear.
  const parseIntOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const computeHighestMarks = (criterias) =>
    (criterias || []).reduce((max, c) => {
      const n = parseIntOrNull(c?.marks);
      return n !== null && n > max ? n : max;
    }, 0);

  const handleHighestMarksChange = (value) => {
    setIsNavigationBlocked(true);
    // Store raw value; saveShortAnswer parses before sending.
    setShortAnswer({ ...shortAnswer, highestMarks: value });
  };

  const handleMarksChange = (index, value) => {
    setIsNavigationBlocked(true);
    const newCriterias = [...shortAnswer.criterias];
    // Pass through the raw input value so clearing the field renders as
    // empty, not "0". Parsing + coercion happens on save.
    newCriterias[index] = { ...newCriterias[index], marks: value };
    setShortAnswer({
      ...shortAnswer,
      criterias: newCriterias,
      highestMarks: computeHighestMarks(newCriterias),
    });
  };

  const handleCriteriaChange = (index, value) => {
    setIsNavigationBlocked(true);
    const newCriterias = [...shortAnswer.criterias];
    newCriterias[index] = { ...newCriterias[index], criteria: value };
    setShortAnswer({ ...shortAnswer, criterias: newCriterias });
  };

  const handleDeleteCriteria = (index) => {
    setIsNavigationBlocked(true);
    const newCriterias = shortAnswer.criterias.filter((_, i) => i !== index);
    setShortAnswer({
      ...shortAnswer,
      criterias: newCriterias,
      highestMarks: computeHighestMarks(newCriterias),
    });
  };

  const handleAddCriteria = () => {
    setIsNavigationBlocked(true);
    const newCriterias = [...shortAnswer.criterias, { marks: 0, criteria: '' }];
    setShortAnswer({ ...shortAnswer, criterias: newCriterias });
  };

  const handleMarksFromChange = (index, value) => {
    setIsNavigationBlocked(true);
    const newCriterias = [...shortAnswer.criterias];
    // Pass through raw input; saveShortAnswer parses into a nullable int.
    newCriterias[index] = { ...newCriterias[index], marksFrom: value };
    setShortAnswer({ ...shortAnswer, criterias: newCriterias });
  };

  const toggleRangesEnabled = () => {
    setIsNavigationBlocked(true);
    if (rangesEnabled) {
      // Turning off: strip marksFrom from every criterion so the next save
      // doesn't sneak in a stale range.
      const newCriterias = shortAnswer.criterias.map(({ marksFrom, ...rest }) => rest);
      setShortAnswer({ ...shortAnswer, criterias: newCriterias });
      setExpandedRangeRows(new Set());
    }
    setRangesEnabled((prev) => !prev);
  };

  const toggleRowRange = (index) => {
    setIsNavigationBlocked(true);
    setExpandedRangeRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const removeRowRange = (index) => {
    setIsNavigationBlocked(true);
    setExpandedRangeRows((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    const newCriterias = [...shortAnswer.criterias];
    const { marksFrom: _dropped, ...rest } = newCriterias[index] || {};
    newCriterias[index] = rest;
    setShortAnswer({ ...shortAnswer, criterias: newCriterias });
  };

  const updateShortAnswer = (updates) => {
    setIsNavigationBlocked(true);
    setShortAnswer({ ...shortAnswer, ...updates });
  };

  // Helper to convert subjects to object format with {subjectCode, subjectName}
  const subjectsAsObjects = (subjects) => {
    if (!Array.isArray(subjects)) return [];
    return subjects.map((s) => {
      // If already in correct format
      if (typeof s === 'object' && s !== null && s.subjectCode && s.subjectName) {
        return s;
      }
      // If object but missing fields
      if (typeof s === 'object' && s !== null) {
        return {
          subjectCode: s.subjectCode || s.code || s,
          subjectName: s.subjectName || s.name || s.title || s
        };
      }
      // If string, look up in subjectsList
      const subjectObj = subjectsList.find((sub) =>
        (sub.subjectCode || sub.code) === s || (sub.subjectName || sub.name || sub.title) === s
      );
      if (subjectObj) {
        return {
          subjectCode: subjectObj.subjectCode || subjectObj.code || s,
          subjectName: subjectObj.subjectName || subjectObj.name || subjectObj.title || s
        };
      }
      return null;
    }).filter(Boolean);
  };

  const saveShortAnswer = (settings = {}) => {
    const years = settings.years || shortAnswer.years || [];
    const rawSubjects = settings.subjects || shortAnswer.subjects || [];
    const taskTypes = settings.taskTypes || shortAnswer.taskTypes || (shortAnswer.taskType ? [shortAnswer.taskType] : []);
    const responseTypes = settings.responseTypes || shortAnswer.responseTypes || (shortAnswer.responseType ? [shortAnswer.responseType] : []);
    const metadata = buildMetadata(userStates, { years, subjects: rawSubjects, taskTypes, responseTypes }, subjectsList);

    // Convert subjects to object format for API
    const subjectsForApi = subjectsAsObjects(rawSubjects);

    // Inputs store raw strings during editing; normalize to ints here so
    // the API receives numbers. Empty -> 0 on highestMarks / marks so the
    // server sees an intentional "no marks" rather than a missing field.
    const normalizedHighest = parseIntOrNull(shortAnswer.highestMarks);
    const normalizedCriterias = (shortAnswer.criterias || []).map((c) => {
      const parsedMarks = parseIntOrNull(c?.marks);
      const parsedMarksFrom = parseIntOrNull(c?.marksFrom);
      const { marksFrom: _drop, ...rest } = c || {};
      return {
        ...rest,
        marks: parsedMarks === null ? 0 : parsedMarks,
        ...(parsedMarksFrom !== null ? { marksFrom: parsedMarksFrom } : {}),
      };
    });

    const newShortAnswer = {
      title: shortAnswer.title,
      highestMarks: normalizedHighest === null ? 0 : normalizedHighest,
      criterias: normalizedCriterias,
      years,
      taskTypes,
      responseTypes,
      metadata,
    };

    if (years.length === 0 || rawSubjects.length === 0) {
      toast(<Toast message="Please select at least one Year and one Subject" />);
      return;
    }
    if (!newShortAnswer.title.trim()) {
      toast(<Toast message="Please add a title for your short answer" />);
      return;
    }

    if (newShortAnswer.criterias.length === 0) {
      toast(<Toast message="Please add at least one criteria" />);
      return;
    }

    for (const c of newShortAnswer.criterias) {
      if (c.marksFrom === null || c.marksFrom === undefined) continue;
      if (!Number.isInteger(c.marksFrom) || c.marksFrom < 0) {
        toast(<Toast message="Mark range start must be a non-negative integer" />);
        return;
      }
      if (Number.isInteger(c.marks) && c.marksFrom >= c.marks) {
        toast(<Toast message="Mark range start must be less than marks" />);
        return;
      }
    }

    createShortAnswer(newShortAnswer)
      .then((response) => {
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'shortAnswers'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'] });
        resetShortAnswers();

        setIsNavigationBlocked(false);

        toast(<Toast message="Short answer created" />);

        if (response?.id) {
          history.push(`/tools/shortanswers/${response.id}`);
        } else {
          history.push('/tools/shortanswers');
        }
      })
      .catch((error) => {
        console.error('Error saving short answer:', error);
        toast(<Toast message="Error saving short answer" />);
      });
  };

  return (
    <>
      {isShowNavigationBlockPopup && (
        <GeneralPopup
          hidePopup={cancelNavigationPopup}
          title="Unsaved Changes"
          textContent="You have unsaved changes in your short answer. If you leave this page, your changes will be lost. Do you want to continue?"
          buttonText="Leave Without Saving"
          confirmButtonAction={confirmNavigationChange}
          closeBtnText="Stay on Page"
          cancelPopup={cancelNavigationPopup}
        />
      )}
      <CreateNewShortAnswerDesktop
        shortAnswer={shortAnswer}
        updateShortAnswer={updateShortAnswer}
        handleTitleChange={handleTitleChange}
        handleMarksChange={handleMarksChange}
        handleCriteriaChange={handleCriteriaChange}
        handleDeleteCriteria={handleDeleteCriteria}
        handleAddCriteria={handleAddCriteria}
        handleHighestMarksChange={handleHighestMarksChange}
        handleMarksFromChange={handleMarksFromChange}
        rangesEnabled={rangesEnabled}
        toggleRangesEnabled={toggleRangesEnabled}
        expandedRangeRows={expandedRangeRows}
        toggleRowRange={toggleRowRange}
        removeRowRange={removeRowRange}
        saveShortAnswer={saveShortAnswer}
        isUpdating={false}
        shortAnswerId="new"
      />
    </>
  );
}
